import {
  Avatar,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import VerifiedIcon from "@mui/icons-material/Verified";
import MessagesProfileComponent from "../Messages/MessagesProfileComponent";
import { useState, useEffect, useMemo } from "react";
import SelectLanguageComponent from "../SelectLanguageComponent";
import { Edit as EditIcon, DeleteOutline as DeleteIcon, Add as AddIcon, Create as WriteIcon } from '@mui/icons-material';
import api from "../../config/axios";

function ProfileSection() {
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openAboutDialog, setOpenAboutDialog] = useState(false);
  const [openServicesDialog, setOpenServicesDialog] = useState(false);
  const [openExperienceDialog, setOpenExperienceDialog] = useState(false);
  const [openDescriptionDialog, setOpenDescriptionDialog] = useState(false);
  const [openEducationDialog, setOpenEducationDialog] = useState(false);
  
  // Form states
  const [aboutForm, setAboutForm] = useState("");
  const [serviceForm, setServiceForm] = useState({
    title: "",
    description: "",
    category: ""
  });
  const [experienceForm, setExperienceForm] = useState({
    jobTitle: "",
    companyName: "",
    employmentType: "Full-time",
    location: "",
    isRemote: false,
    startDate: "",
    endDate: "",
    isCurrentJob: false,
    description: ""
  });
  const [descriptionForm, setDescriptionForm] = useState("");
  const [educationForm, setEducationForm] = useState({
    university: "",
    degree: "",
    startYear: "",
    endYear: "",
  });

  const profileStats = [
    { count: userData?.postsCount?.toLocaleString() || "0", statName: "Posts" },
    { count: userData?.followersCount?.toLocaleString() || "0", statName: "Followers" },
    { count: userData?.followingCount?.toLocaleString() || "0", statName: "Following" }
  ];

  const handleDeleteEducation = async (educationId) => {
    try {
      await api.delete(`/auth/education/${educationId}`);
      await fetchUserData();
    } catch (error) {
      console.error("Error deleting education:", error);
      alert("Failed to delete education");
    }
  };

  const profileRightstats = userData ? [
    { 
      icon: "/images/navbar-profile-pic.jpeg", 
      text: userData?.education?.[0]?.degree || "No Degree Added",
      id: userData?.education?.[0]?._id,
      actions: userData?.education?.[0] && (
        <IconButton 
          size="small" 
          onClick={() => handleDeleteEducation(userData.education[0]._id)}
        >
          <DeleteIcon sx={{ fontSize: 16 }} />
        </IconButton>
      )
    },
    { 
      icon: "/images/navbar-profile-pic.jpeg", 
      text: userData?.education?.[0]?.university || "No University Added",
      id: userData?.education?.[0]?._id,
      actions: userData?.education?.[0] && (
        <IconButton 
          size="small" 
          onClick={() => handleDeleteEducation(userData.education[0]._id)}
        >
          <DeleteIcon sx={{ fontSize: 16 }} />
        </IconButton>
      )
    }
  ] : [];

  const profileButtons = ["Add Profile Section", "More"];

  const experienceData = userData?.experience?.map(exp => ({
    primaryText: exp.jobTitle,
    secondaryText: `${exp.companyName} · ${exp.employmentType} <br/> ${new Date(exp.startDate).toLocaleDateString()} to ${exp.isCurrentJob ? 'Present' : new Date(exp.endDate).toLocaleDateString()} <br/> ${exp.location} ${exp.isRemote ? '· Remote' : ''}`,
    experienceDescription: [exp.description],
    divider: true,
    actions: (
      <Stack direction="row" spacing={1}>
        <IconButton 
          size="small" 
          onClick={() => handleEditExistingExperience(exp)}
        >
          <EditIcon sx={{ fontSize: 16 }} />
        </IconButton>
        <IconButton 
          size="small" 
          onClick={() => handleDeleteExperience(exp._id)}
        >
          <DeleteIcon sx={{ fontSize: 16 }} />
        </IconButton>
      </Stack>
    )
  })) || [];

  useEffect(() => {
    fetchUserData();
  }, []);

  const fetchUserData = async () => {
    try {
      const response = await api.get("/auth/me");
      console.log("Full user data:", response.data);
      console.log("Services data:", {
        services: response.data.services,
        hasServices: Array.isArray(response.data.services) && response.data.services.length > 0
      });
      setUserData(response.data);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching user data:", error);
      setLoading(false);
    }
  };

  const validateImageSize = (file) => {
    const maxSize = 2 * 1024 * 1024; // 2MB in bytes
    if (file.size > maxSize) {
      alert("Image size must be less than 2MB");
      return false;
    }
    return true;
  };

  const handleImageUpload = async (event, type) => {
    const file = event.target.files[0];
    if (!file) return;

    // Check if token exists
    const token = localStorage.getItem('token');
    if (!token) {
      alert('You are not logged in. Please log in again.');
      // You might want to redirect to login page here
      return;
    }

    console.log('Current token:', token);
    console.log('File selected:', {
      name: file.name,
      type: file.type,
      size: `${(file.size / 1024).toFixed(2)}KB`
    });

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/jpg'];
    if (!allowedTypes.includes(file.type)) {
      alert("Please upload a valid image file (JPEG, PNG, GIF, or JPG)");
      event.target.value = ''; // Clear the input
      return;
    }

    // Validate file size
    if (!validateImageSize(file)) {
      event.target.value = ''; // Clear the input
      return;
    }

    // Convert file to base64
    const reader = new FileReader();
    reader.onloadend = async () => {
      try {
        const base64String = reader.result;
        console.log(`Attempting to upload ${type} image`);
        console.log('Image type:', file.type);
        console.log('Base64 string starts with:', base64String.substring(0, 50) + '...');
        
        // Create the request body with only the specific image type
        const requestBody = {};
        requestBody[type] = base64String;
        
        // Log the request configuration
        console.log('Request headers:', {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        });
        
        const response = await api.put("/auth/profile-images", requestBody);
        console.log('Upload response:', response.data);
        
        if (response.data) {
          console.log(`${type} updated successfully`);
          await fetchUserData(); // Refresh user data
          console.log('User data refreshed');
        }
      } catch (error) {
        console.error("Error uploading image:", {
          message: error.message,
          response: error.response?.data,
          status: error.response?.status,
          type: type
        });
        
        // Check if it's an authentication error
        if (error.response?.status === 401) {
          localStorage.removeItem('token'); // Clear invalid token
          alert('Your session has expired. Please log in again.');
          // You might want to redirect to login page here
          return;
        }
        
        let errorMessage = "Failed to update image. ";
        if (error.response?.data?.message) {
          errorMessage += error.response.data.message;
        } else if (error.message) {
          errorMessage += error.message;
        } else {
          errorMessage += "Please try again.";
        }
        
        alert(errorMessage);
      }
      // Clear the input after upload attempt (success or failure)
      event.target.value = '';
    };

    reader.onerror = (error) => {
      console.error("FileReader error:", error);
      alert("Error reading file. Please try again.");
      event.target.value = ''; // Clear the input
    };

    reader.readAsDataURL(file);
  };

  const handleEditAbout = async () => {
    setAboutForm(userData?.about || "");
    setOpenAboutDialog(true);
  };

  const handleSaveAbout = async () => {
    try {
      console.log("Saving about data:", aboutForm);
      const response = await api.put("/auth/about", { about: aboutForm });
      console.log("Save about response:", response.data);
      await fetchUserData();
      setOpenAboutDialog(false);
    } catch (error) {
      console.error("Error updating about:", error);
      alert("Failed to update about section");
    }
  };

  const handleEditServices = () => {
    setServiceForm({
      title: "",
      description: "",
      category: ""
    });
    setOpenServicesDialog(true);
  };

  const handleDeleteService = async (serviceId) => {
    try {
      await api.delete(`/auth/services/${serviceId}`);
      await fetchUserData();
    } catch (error) {
      console.error("Error deleting service:", error);
      alert("Failed to delete service");
    }
  };

  const handleEditExistingService = (service) => {
    setServiceForm({
      id: service._id,
      title: service.title,
      description: service.description || "",
      category: service.category || ""
    });
    setOpenServicesDialog(true);
  };

  const handleSaveService = async () => {
    try {
      console.log("Saving service:", serviceForm);
      const serviceData = {
        title: serviceForm.title.trim(),
        description: serviceForm.description.trim(),
        category: serviceForm.category.trim()
      };

      let response;
      if (serviceForm.id) {
        // Update existing service
        response = await api.put(`/auth/services/${serviceForm.id}`, { service: serviceData });
      } else {
        // Add new service
        response = await api.put("/auth/services", { services: serviceData });
      }

      console.log("Service save response:", response.data);
      await fetchUserData();
      setOpenServicesDialog(false);
      setServiceForm({
        title: "",
        description: "",
        category: ""
      });
    } catch (error) {
      console.error("Error saving service:", error);
      alert("Failed to save service: " + (error.response?.data?.message || error.message));
    }
  };

  const handleEditExperience = () => {
    setExperienceForm({
      jobTitle: "",
      companyName: "",
      employmentType: "Full-time",
      location: "",
      isRemote: false,
      startDate: "",
      endDate: "",
      isCurrentJob: false,
      description: ""
    });
    setOpenExperienceDialog(true);
  };

  const handleDeleteExperience = async (experienceId) => {
    try {
      await api.delete(`/auth/experience/${experienceId}`);
      await fetchUserData();
    } catch (error) {
      console.error("Error deleting experience:", error);
      alert("Failed to delete experience");
    }
  };

  const handleEditExistingExperience = (experience) => {
    setExperienceForm({
      id: experience._id,
      jobTitle: experience.jobTitle,
      companyName: experience.companyName,
      employmentType: experience.employmentType,
      location: experience.location || "",
      isRemote: experience.isRemote || false,
      startDate: new Date(experience.startDate).toISOString().split('T')[0],
      endDate: experience.endDate ? new Date(experience.endDate).toISOString().split('T')[0] : "",
      isCurrentJob: experience.isCurrentJob || false,
      description: experience.description || ""
    });
    setOpenExperienceDialog(true);
  };

  const handleSaveExperience = async () => {
    try {
      const experienceData = {
        ...experienceForm,
        endDate: experienceForm.isCurrentJob ? undefined : experienceForm.endDate
      };
      delete experienceData.id;  // Remove id before sending to API

      let response;
      if (experienceForm.id) {
        // Update existing experience
        response = await api.put(`/auth/experience/${experienceForm.id}`, { experience: experienceData });
      } else {
        // Add new experience
        response = await api.put("/auth/experience", { experience: experienceData });
      }

      await fetchUserData();
      setOpenExperienceDialog(false);
      setExperienceForm({
        jobTitle: "",
        companyName: "",
        employmentType: "Full-time",
        location: "",
        isRemote: false,
        startDate: "",
        endDate: "",
        isCurrentJob: false,
        description: ""
      });
    } catch (error) {
      console.error("Error saving experience:", error);
      alert("Failed to save experience: " + (error.response?.data?.message || error.message));
    }
  };

  const handleEditDescription = () => {
    setDescriptionForm(userData?.description || "");
    setOpenDescriptionDialog(true);
  };

  const handleSaveDescription = async () => {
    try {
      console.log("Saving description:", descriptionForm);
      const response = await api.put("/auth/about", { description: descriptionForm });
      console.log("Save description response:", response.data);
      await fetchUserData();
      setOpenDescriptionDialog(false);
    } catch (error) {
      console.error("Error updating description:", error);
      alert("Failed to update description");
    }
  };

  const handleEditEducation = () => {
    setEducationForm({
      university: "",
      degree: "",
      startYear: "",
      endYear: "",
    });
    setOpenEducationDialog(true);
  };

  const handleEditExistingEducation = (education) => {
    setEducationForm({
      id: education._id,
      university: education.university,
      degree: education.degree,
      startYear: education.startYear,
      endYear: education.endYear,
    });
    setOpenEducationDialog(true);
  };

  const handleSaveEducation = async () => {
    try {
      const educationData = {
        university: educationForm.university.trim(),
        degree: educationForm.degree.trim(),
        startYear: parseInt(educationForm.startYear),
        endYear: parseInt(educationForm.endYear),
        fieldOfStudy: educationForm.degree.trim(),
      };

      let response;
      if (educationForm.id) {
        response = await api.put(`/auth/education/${educationForm.id}`, { education: educationData });
      } else {
        response = await api.put("/auth/education", { education: educationData });
      }

      console.log("Education save response:", response.data);
      await fetchUserData();
      setOpenEducationDialog(false);
      setEducationForm({
        university: "",
        degree: "",
        startYear: "",
        endYear: "",
      });
    } catch (error) {
      console.error("Error saving education:", error.response?.data || error);
      alert("Failed to save education: " + (error.response?.data?.message || error.message));
    }
  };

  // Move aboutPortionData here, after all handlers are defined
  const aboutPortionData = useMemo(() => [
    {
      title: "About",
      descriptions: [userData?.about || "No about information added"],
      editHandler: handleEditAbout
    },
    {
      title: "Services",
      descriptions: userData?.services && userData.services.length > 0
        ? userData.services.map(service => ({
            text: `${service.title}${service.description ? `: ${service.description}` : ''}`,
            id: service._id,
            service: service,
            actions: (
              <Stack direction="row" spacing={1}>
                <IconButton 
                  size="small" 
                  onClick={() => handleEditExistingService(service)}
                >
                  <EditIcon sx={{ fontSize: 16 }} />
                </IconButton>
                <IconButton 
                  size="small" 
                  onClick={() => handleDeleteService(service._id)}
                >
                  <DeleteIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </Stack>
            )
          }))
        : ["No services added"],
      editHandler: handleEditServices
    },
    {
      title: "Education",
      descriptions: userData?.education && userData.education.length > 0
        ? userData.education.map(edu => ({
            text: `${edu.degree} at ${edu.university} (${edu.startYear}-${edu.endYear})`,
            id: edu._id,
            actions: (
              <Stack direction="row" spacing={1}>
                <IconButton 
                  size="small" 
                  onClick={() => handleEditExistingEducation(edu)}
                >
                  <EditIcon sx={{ fontSize: 16 }} />
                </IconButton>
                <IconButton 
                  size="small" 
                  onClick={() => handleDeleteEducation(edu._id)}
                >
                  <DeleteIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </Stack>
            )
          }))
        : ["No education added"],
      editHandler: handleEditEducation
    },
  ], [userData?.about, userData?.services, userData?.education]);

  if (loading) {
    return <div>Loading profile...</div>;
  }

  return (
    <>
      <Stack
        sx={{
          width: "100%",
          minWidth: "72%",
          bgcolor: "#ffffff",
          p: "20px",
          borderRadius: "22px",
        }}
      >
        {" "}
        <Box
          sx={{
            backgroundImage: `url(${userData?.coverImage || "/images/desert-pattern.jpeg"})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
            backgroundRepeat: "no-repeat",
            height: "231px",
            borderRadius: "12px",
            display: "flex",
            alignItems: "end",
            position: "relative",
            mb: "90px",
          }}
        >
          {/* Cover Image Upload Button */}
          <input
            accept="image/*"
            style={{ display: 'none' }}
            id="cover-image-upload"
            type="file"
            onChange={(e) => handleImageUpload(e, 'coverImage')}
          />
          <label htmlFor="cover-image-upload">
            <IconButton
              component="span"
              sx={{
                position: 'absolute',
                top: 10,
                right: 10,
                bgcolor: 'rgba(255, 255, 255, 0.8)',
                '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.9)' },
              }}
            >
              <EditIcon />
            </IconButton>
          </label>

          {/* Profile Image Upload */}
          <input
            accept="image/*"
            style={{ display: 'none' }}
            id="profile-image-upload"
            type="file"
            onChange={(e) => handleImageUpload(e, 'profileImage')}
          />
          <label htmlFor="profile-image-upload">
            <Avatar
              src={userData?.profileImage || "/images/navbar-profile-pic.jpeg"}
              alt="Profile Picture"
              sx={{
                width: 156,
                height: 156,
                objectFit: "cover",
                border: "9px solid #ffffff",
                position: "absolute",
                bottom: -85,
                ml: "18px",
                cursor: "pointer",
                '&:hover': {
                  '&::after': {
                    content: '"Edit"',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    bgcolor: 'rgba(0, 0, 0, 0.5)',
                    color: 'white',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderRadius: '50%',
                  }
                }
              }}
            />
          </label>
        </Box>
        <Box sx={{ pl: "24px", pt: "0px", position: "relative" }}>
          {" "}
          <Typography
            variant="body2"
            sx={{
              fontWeight: "500",
              display: "flex",
              alignItems: "center",
              gap: "8px",
              fontSize: "35px",
              lineHeight: "42.36px",
            }}
          >
            {userData ? `${userData.firstName} ${userData.lastName}` : "Guest"}{" "}
            <Box
              component="img"
              src="/icons/bluetick.svg"
              sx={{ width: "24px", height: "24px" }}
            ></Box>
          </Typography>
          <Stack sx={{ gap: "8px", position: "absolute", right: 20, top: -8 }}>
            {profileRightstats.map((stat) => (
              <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }} sp>
                <Avatar
                  src={stat.icon}
                  alt="Profile Picture"
                  sx={{
                    width: 24,
                    height: 24,
                    objectFit: "cover",
                  }}
                />
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography
                    variant="body2"
                    sx={{
                      fontSize: "18px",
                      lineHeight: "21.78px",
                      fontWeight: "400px",
                    }}
                  >
                    {stat.text}
                  </Typography>
                  {stat.actions}
                </Box>
              </Box>
            ))}
          </Stack>
          <Stack id="profile-stats" direction="row" gap={5} sx={{ py: "8px" }}>
            {profileStats.map((stat, i) => (
              <Stack
                direction={"column"}
                justifyContent={"center"}
                alignItems="center"
              >
                <Typography
                  variant="subtite2"
                  sx={{
                    fontWeight: "600",
                    fontSize: "20px",
                    lineHeight: "24.2px",
                  }}
                >
                  {stat.count}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    color: "#8c8c8c",
                    fontSize: "16px",
                    lineHeight: "19.36px",
                    fontWeight: "400",
                  }}
                >
                  {stat.statName}
                </Typography>
              </Stack>
            ))}
          </Stack>
          <Typography
            variant="body2"
            sx={{
              color: "#8c8c8c",
              fontSize: "16px",
              lineHeight: "19.36px",
              fontWeight: "400",
              pb: "0px",
              pt: "14px",
            }}
          >
            UI / UX Designer
          </Typography>
          <Typography
            variant="body2"
            sx={{
              fontSize: "17px",
              lineHeight: "24px",
              fontWeight: "500px",
              width: "595px",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "start"
            }}
          >
            <span>
              {userData?.description || "No description added"}
            </span>
            <IconButton sx={{ p: "0px" }} onClick={handleEditDescription}>
              {userData?.description ? (
                <WriteIcon sx={{ fontSize: 18 }} />
              ) : (
                <AddIcon sx={{ fontSize: 18 }} />
              )}
            </IconButton>
          </Typography>
          <Box sx={{ display: "flex", gap: "15px", mt: "22px" }}>
            <Button
              variant="contained"
              sx={{
                alignSelf: "start",
                borderRadius: "28px",
                px: "20px",
                minWidth: "auto",
                width: "auto",
                textTransform: "none",
                bgcolor: "#000000",
                color: "#FFFFFF",
                boxShadow: "none",
                py: "8px",

                "&:hover": {
                  backgroundColor: "#000000",
                  boxShadow: "none",
                },
                "&:active": {
                  backgroundColor: "#000000",
                  boxShadow: "none",
                },
                "&:focus": {
                  outline: "none",
                  boxshadow: "none",
                },
                "& .MuiButtonBase-root": {
                  width: "auto !important",
                  minWidth: "0px !important",
                  padding: "0px",
                  boxshadow: "none",
                },
              }}
            >
              Improve Profile
            </Button>
            {profileButtons.map((button) => (
              <Button
                variant="contained"
                sx={{
                  py: "6px",
                  alignSelf: "start",
                  borderRadius: "20.5px",
                  px: "20px",
                  minWidth: "auto",
                  width: "auto",
                  textTransform: "none",
                  bgcolor: "transparent",
                  color: "#000000",
                  boxShadow: "none",
                  border: "1px solid #000000",
                  "&:hover": {
                    backgroundColor: "transparent",
                    boxShadow: "none",
                  },
                  "&:active": {
                    backgroundColor: "transparent",
                    boxShadow: "none",
                  },
                  "&:focus": {
                    outline: "none",
                    boxshadow: "none",
                  },
                  "& .MuiButtonBase-root": {
                    width: "auto !important",
                    minWidth: "0px !important",
                    padding: "0px",
                    boxshadow: "none",
                  },
                }}
              >
                {button}
              </Button>
            ))}
          </Box>
        </Box>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: "14px",
            mt: "50px",
          }}
        >
          {aboutPortionData.map((data) => (
            <Stack
              key={data.title}
              sx={{
                bgcolor: "#f8f8f8",
                p: "20px",
                px: "28px",
                borderRadius: "12px",
                border: "1px solid #e3e3e3",
              }}
            >
              <Typography
                variant="h6"
                sx={{
                  fontWeight: "600",
                  lineHeight: "24.2px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  pb: "8px",
                }}
              >
                {data.title}
                <IconButton sx={{ p: "0px" }} onClick={data.editHandler}>
                  {data.title === "About" && userData?.about ? (
                    <WriteIcon sx={{ fontSize: 18 }} />
                  ) : (
                    <AddIcon sx={{ fontSize: 18 }} />
                  )}
                </IconButton>
              </Typography>
              {data.descriptions.map((description, index) => (
                <Box
                  key={index}
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    pb: "16px",
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      fontSize: "15px",
                      lineHeight: "18.15px",
                      maxWidth: "1000px",
                    }}
                  >
                    {description.text || description}
                  </Typography>
                  {description.actions && description.actions}
                </Box>
              ))}
            </Stack>
          ))}
          <Stack
            sx={{
              bgcolor: "#f8f8f8",
              p: "20px",
              px: "28px",
              borderRadius: "12px",
              border: "1px solid #e3e3e3",
            }}
          >
            <Typography
              variant="h6"
              sx={{
                fontWeight: "600",
                lineHeight: "24.2px",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                pb: "8px",
              }}
            >
              Experience
              <IconButton sx={{ p: "0px" }} onClick={handleEditExperience}>
                <AddIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Typography>
            {experienceData.map((experience, index) => (
              <Box key={index}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', mb: 2 }}>
                  <MessagesProfileComponent
                    primaryText={experience.primaryText}
                    secondaryText={experience.secondaryText}
                    chatConservation={true}
                  />
                  {experience.actions}
                </Box>
                <Box sx={{ pt: "20px" }}>
                  {experience.experienceDescription.map((description) => (
                    <Typography
                      variant="body2"
                      sx={{
                        fontSize: "15px",
                        lineHeight: "18.15px",
                        maxWidth: "930px",
                        pb: "16px",
                        pl: "68px",
                      }}
                    >
                      {description}
                    </Typography>
                  ))}
                  {experience.divider && index < experienceData.length - 1 && (
                    <Divider
                      sx={{
                        height: "1px",
                        bgcolor: "#D2D2D2",
                        border: "none",
                        my: "18px",
                        mb: "34px",
                      }}
                    />
                  )}
                </Box>
              </Box>
            ))}
          </Stack>
        </Box>
        <SelectLanguageComponent marginTop="50px" />
      </Stack>

      {/* About Dialog */}
      <Dialog open={openAboutDialog} onClose={() => setOpenAboutDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>Edit About</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="About"
            fullWidth
            multiline
            rows={4}
            value={aboutForm}
            onChange={(e) => setAboutForm(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenAboutDialog(false)}>Cancel</Button>
          <Button onClick={handleSaveAbout} variant="contained">Save</Button>
        </DialogActions>
      </Dialog>

      {/* Services Dialog */}
      <Dialog open={openServicesDialog} onClose={() => setOpenServicesDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>Add Service</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Service Title"
              fullWidth
              value={serviceForm.title}
              onChange={(e) => setServiceForm({ ...serviceForm, title: e.target.value })}
            />
            <TextField
              label="Description"
              fullWidth
              multiline
              rows={3}
              value={serviceForm.description}
              onChange={(e) => setServiceForm({ ...serviceForm, description: e.target.value })}
            />
            <TextField
              label="Category"
              fullWidth
              value={serviceForm.category}
              onChange={(e) => setServiceForm({ ...serviceForm, category: e.target.value })}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenServicesDialog(false)}>Cancel</Button>
          <Button onClick={handleSaveService} variant="contained">Save</Button>
        </DialogActions>
      </Dialog>

      {/* Experience Dialog */}
      <Dialog open={openExperienceDialog} onClose={() => setOpenExperienceDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>Add Experience</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Job Title"
              fullWidth
              value={experienceForm.jobTitle}
              onChange={(e) => setExperienceForm({ ...experienceForm, jobTitle: e.target.value })}
            />
            <TextField
              label="Company Name"
              fullWidth
              value={experienceForm.companyName}
              onChange={(e) => setExperienceForm({ ...experienceForm, companyName: e.target.value })}
            />
            <FormControl fullWidth>
              <InputLabel>Employment Type</InputLabel>
              <Select
                value={experienceForm.employmentType}
                label="Employment Type"
                onChange={(e) => setExperienceForm({ ...experienceForm, employmentType: e.target.value })}
              >
                <MenuItem value="Full-time">Full-time</MenuItem>
                <MenuItem value="Part-time">Part-time</MenuItem>
                <MenuItem value="Contract">Contract</MenuItem>
                <MenuItem value="Freelance">Freelance</MenuItem>
                <MenuItem value="Internship">Internship</MenuItem>
              </Select>
            </FormControl>
            <TextField
              label="Location"
              fullWidth
              value={experienceForm.location}
              onChange={(e) => setExperienceForm({ ...experienceForm, location: e.target.value })}
            />
            <FormControl fullWidth>
              <InputLabel>Remote Work</InputLabel>
              <Select
                value={experienceForm.isRemote}
                label="Remote Work"
                onChange={(e) => setExperienceForm({ ...experienceForm, isRemote: e.target.value })}
              >
                <MenuItem value={true}>Yes</MenuItem>
                <MenuItem value={false}>No</MenuItem>
              </Select>
            </FormControl>
            <TextField
              label="Start Date"
              type="date"
              fullWidth
              value={experienceForm.startDate}
              onChange={(e) => setExperienceForm({ ...experienceForm, startDate: e.target.value })}
              InputLabelProps={{ shrink: true }}
            />
            <FormControl fullWidth>
              <InputLabel>Current Job</InputLabel>
              <Select
                value={experienceForm.isCurrentJob}
                label="Current Job"
                onChange={(e) => setExperienceForm({ ...experienceForm, isCurrentJob: e.target.value })}
              >
                <MenuItem value={true}>Yes</MenuItem>
                <MenuItem value={false}>No</MenuItem>
              </Select>
            </FormControl>
            {!experienceForm.isCurrentJob && (
              <TextField
                label="End Date"
                type="date"
                fullWidth
                value={experienceForm.endDate}
                onChange={(e) => setExperienceForm({ ...experienceForm, endDate: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
            )}
            <TextField
              label="Description"
              fullWidth
              multiline
              rows={4}
              value={experienceForm.description}
              onChange={(e) => setExperienceForm({ ...experienceForm, description: e.target.value })}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenExperienceDialog(false)}>Cancel</Button>
          <Button onClick={handleSaveExperience} variant="contained">Save</Button>
        </DialogActions>
      </Dialog>

      {/* Description Dialog */}
      <Dialog open={openDescriptionDialog} onClose={() => setOpenDescriptionDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>Edit Description</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Description"
            fullWidth
            multiline
            rows={4}
            value={descriptionForm}
            onChange={(e) => setDescriptionForm(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDescriptionDialog(false)}>Cancel</Button>
          <Button onClick={handleSaveDescription} variant="contained">Save</Button>
        </DialogActions>
      </Dialog>

      {/* Education Dialog */}
      <Dialog open={openEducationDialog} onClose={() => setOpenEducationDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>{educationForm.id ? "Edit Education" : "Add Education"}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="University/Institution"
              fullWidth
              value={educationForm.university}
              onChange={(e) => setEducationForm({ ...educationForm, university: e.target.value })}
            />
            <TextField
              label="Degree/Program"
              fullWidth
              value={educationForm.degree}
              onChange={(e) => setEducationForm({ ...educationForm, degree: e.target.value })}
            />
            <Stack direction="row" spacing={2}>
              <TextField
                label="Start Year"
                type="number"
                fullWidth
                value={educationForm.startYear}
                onChange={(e) => setEducationForm({ ...educationForm, startYear: e.target.value })}
                InputProps={{ inputProps: { min: 1900, max: new Date().getFullYear() } }}
              />
              <TextField
                label="End Year"
                type="number"
                fullWidth
                value={educationForm.endYear}
                onChange={(e) => setEducationForm({ ...educationForm, endYear: e.target.value })}
                InputProps={{ inputProps: { min: 1900, max: new Date().getFullYear() + 10 } }}
              />
            </Stack>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenEducationDialog(false)}>Cancel</Button>
          <Button onClick={handleSaveEducation} variant="contained">Save</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default ProfileSection;
